// The verification a document is linked to, with what the completion prompt
// needs. carrierRouting reads the carrier directory per row: use this only on
// per-applicant document lists, never company-wide ones.
export const DOCUMENT_VOE_LINK_FIELDS = gql`
  fragment DocumentVoELinkFields on VerificationOfEmployment {
    voeId
    status
    companyName
    driverName
    applicant {
      applicantId
      firstName
      lastName
    }
    contactName
    email
    phoneNumber
    faxNumber
    dotNumber
    platform
    carrierRouting {
      preferredPlatform
    }
  }
`

export const GET_DOCUMENTS = gql`
  ${DOCUMENT_VOE_LINK_FIELDS}
  query GetDocuments(
    $applicantId: String!
    $filters: DocumentFiltersInput
    $pageInfo: PageInfoInput
  ) {
    documents(
      applicantId: $applicantId
      filters: $filters
      pageInfo: $pageInfo
    ) {
      totalCount
      items {
        documentId
        createdAt
        updatedAt
        status
        category
        userStatus
        fileName
        fileType
        path
        expirationDate
        isRenewed
        step {
          stepId
          type
          customStepId
          customStep {
            label
          }
        }
        uploadedBy {
          employeeId
          firstName
          lastName
        }
        voe {
          ...DocumentVoELinkFields
        }
        expirationTrackers {
          expirationTrackerId
          status
        }
        documentIntelligence {
          status
          predictions {
            field
            confidence
            autoApplied
            expirationValue
            categoryValue
            reviewDecision
          }
          findings {
            findingGroupId
            findingType
            fields {
              field
              value
            }
          }
        }
      }
    }
  }
`

export const CREATE_DOCUMENT = gql`
  mutation CreateDocument($input: CreateDocumentInput!) {
    createDocument(input: $input) {
      documentId
      uploadUrl
    }
  }
`

export const DELETE_DOCUMENT = gql`
  mutation DeleteDocument($id: String!, $applicantId: String!) {
    deleteDocument(id: $id, applicantId: $applicantId) {
      documentId
    }
  }
`

// Just the ids of every document that matches the filters (no paging), in the
// order the Documents table shows them. Drives previous/next in the preview.
export const GET_DOCUMENT_IDS = gql`
  query GetDocumentIds($applicantId: String!, $filters: DocumentFiltersInput) {
    documents(applicantId: $applicantId, filters: $filters) {
      items {
        documentId
      }
    }
  }
`

export const GET_DOCUMENT_DOWNLOAD_URL = gql`
  query DocumentDownloadUrl($id: String!, $applicantId: String!) {
    documentDownloadUrl(id: $id, applicantId: $applicantId) {
      url
      fileName
      fileType
      userStatus
      category
      expirationDate
      createdAt
      stepId
    }
  }
`

// DownloadUrlResult carries no relations, so the preview fetches the linked
// verification separately.
export const GET_DOCUMENT_VOE_LINK = gql`
  ${DOCUMENT_VOE_LINK_FIELDS}
  query DocumentVoELink($id: String!, $applicantId: String!) {
    document(id: $id, applicantId: $applicantId) {
      documentId
      voe {
        ...DocumentVoELinkFields
      }
    }
  }
`

export const UPDATE_DOCUMENT = gql`
  mutation UpdateDocument($id: String!, $input: UpdateDocumentInput!) {
    updateDocument(id: $id, input: $input) {
      documentId
      createdAt
      updatedAt
      deletedAt
      status
      userStatus
      uploadStatus
      category
      fileType
      expirationDate
      fileName
      path
      uploadUrl
      step {
        stepId
        type
        customStepId
        customStep {
          label
        }
      }
    }
  }
`
export const SET_DOCUMENT_RENEWED = gql`
  mutation SetDocumentRenewed(
    $documentId: String!
    $applicantId: String!
    $isRenewed: Boolean!
  ) {
    setDocumentRenewed(
      documentId: $documentId
      applicantId: $applicantId
      isRenewed: $isRenewed
    ) {
      documentId
      isRenewed
    }
  }
`

export const GET_DOCUMENT_INTELLIGENCE = gql`
  query GetDocumentIntelligence($id: String!, $applicantId: String!) {
    document(id: $id, applicantId: $applicantId) {
      documentId
      category
      status
      expirationDate
      documentIntelligence {
        status
        hasAnyPredictions
        predictions {
          documentPredictionId
          field
          confidence
          autoApplied
          overriddenAfterAutoApply
          expirationValue
          categoryValue
          textValue
          reviewDecision
          findingGroupId
          findingType
        }
        findings {
          findingGroupId
          findingType
          fields {
            field
            value
            confidence
          }
        }
        aiExtractedRows {
          field
          label
          value
          confidence
          reviewStatus
          records {
            date
            location
            description
            details {
              label
              value
            }
          }
        }
      }
    }
  }
`

// MVR finding rows for the Accidents & Violations count pill. Fetches the
// applicant's MVR documents with the backend-assembled aiExtractedRows
// read-model; the web lib (mvrFindingsFromExtractedRows) flattens the finding
// count rows' records into the per-finding shape the pills render. Using the
// read-model means the pills inherit the same per-field display gate as the
// "AI Extracted" popover instead of re-assembling findings client-side.
export const GET_MVR_FINDINGS = gql`
  query GetMvrFindings($applicantId: String!) {
    documents(applicantId: $applicantId, filters: { categories: [MVR] }) {
      items {
        documentId
        createdAt
        category
        documentIntelligence {
          status
          aiExtractedRows {
            field
            value
            records {
              findingType
              date
              location
              description
              cmvInvolved
              injuries
              fatalities
              details {
                label
                value
              }
            }
          }
        }
      }
    }
  }
`

// ENG-4335: PSP mirror of GET_MVR_FINDINGS. Fetches the applicant's PSP
// documents with the backend aiExtractedRows read-model; the web lib
// (pspFindingsFromExtractedRows) flattens each finding-count row's records
// into the per-finding shape the pills render. Same shape as MVR because
// aiExtractedRows is uniform across MVR / PSP finding groups on the backend.
export const GET_PSP_FINDINGS = gql`
  query GetPspFindings($applicantId: String!) {
    documents(applicantId: $applicantId, filters: { categories: [PSP] }) {
      items {
        documentId
        createdAt
        category
        documentIntelligence {
          status
          aiExtractedRows {
            field
            value
            records {
              findingType
              date
              location
              description
              cmvInvolved
              injuries
              fatalities
              details {
                label
                value
              }
            }
          }
        }
      }
    }
  }
`

export const REVIEW_DOCUMENT_PREDICTIONS = gql`
  mutation ReviewDocumentPredictions(
    $documentId: String!
    $decisions: [PredictionReviewInput!]!
  ) {
    reviewDocumentPredictions(documentId: $documentId, decisions: $decisions)
  }
`

export const CREATE_DOCUMENT_EXPORT = gql`
  mutation CreateDocumentExport($input: CreateDocumentExportInput!) {
    createDocumentExport(input: $input) {
      documentExportId
      status
      documentCount
      createdAt
    }
  }
`

export const GET_LICENSE_INTELLIGENCE = gql`
  query GetLicenseIntelligence($applicantId: String!) {
    documents(
      applicantId: $applicantId
      filters: { categories: [CDL, DOT_MEDICAL] }
      pageInfo: { limit: 10, offset: 0 }
    ) {
      items {
        documentId
        category
        documentIntelligence {
          status
          predictions {
            field
            confidence
            expirationValue
            textValue
            reviewDecision
          }
        }
      }
    }
  }
`

export const GET_EXPIRING_DOCUMENT_BY_CATEGORY = gql`
  query DocumentsExpirationByCategory(
    $filters: DocumentsExpirationFilters
    $pageInfo: PageInfoInput
  ) {
    documentsExpirationByCategory(filters: $filters, pageInfo: $pageInfo) {
      items {
        documentId
        fileName
        category
        expirationDate
        applicant {
          applicantId
          firstName
          lastName
          fullName
        }
        expirationTrackers {
          expirationTrackerId
          status
        }
      }
      totalCount
    }
  }
`
